// Production sanitization is the plain DOMPurify default in every mode — no widened allowlist.
// Under prototype mode, and only for a field that opts in with `allowVideo`, a second pass
// upgrades a video carrier anchor (see videoEmbed.ts) back into a playable iframe; everywhere
// else the anchor is left as-is (graceful fallback).
import DOMPurify from 'dompurify';
import { getEffectivePrototypeEnabled } from '../Prototype/prototypeMode';
import { parseVideoCarrierHref } from './videoEmbed';

const CARRIER_IFRAME_WIDTH = '640';
const CARRIER_IFRAME_HEIGHT = '360';

function upgradeVideoCarrierAnchors(html: string): string {
  const container = globalThis.document.createElement('div');
  container.innerHTML = html;

  for (const anchor of [...container.querySelectorAll('a')]) {
    const src = parseVideoCarrierHref(anchor.getAttribute('href'));

    if (!src) {
      continue;
    }

    // src passed parseVideoCarrierHref's host allowlist check above, so building the iframe from
    // it here — rather than from any unvalidated string — is safe by construction.
    const iframe = globalThis.document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.setAttribute('width', CARRIER_IFRAME_WIDTH);
    iframe.setAttribute('height', CARRIER_IFRAME_HEIGHT);
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', anchor.textContent?.trim() || src);

    anchor.replaceWith(iframe);
  }

  return container.innerHTML;
}

interface SanitizeEditorHtmlOptions {
  /** Upgrade video carrier anchors to players. Only the experience description opts in. */
  allowVideo?: boolean;
}

export function sanitizeEditorHtml(
  html: string,
  { allowVideo = false }: SanitizeEditorHtmlOptions = {}
): string {
  const sanitized = DOMPurify.sanitize(html);

  return allowVideo && getEffectivePrototypeEnabled()
    ? upgradeVideoCarrierAnchors(sanitized)
    : sanitized;
}
